import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Check, Moon, Sun, Snowflake, Coffee, Droplets, Pill, BookOpen, Dumbbell, Wind, Heart } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

interface SleepPrepProps {
  onClose: () => void;
}

const checklist = [
  { id: "dim", title: "Dim the Lights", description: "Lower brightness 1 hour before bed to boost melatonin", icon: Sun, color: "from-amber-400 to-orange-400" },
  { id: "screens", title: "Put Away Screens", description: "No phones, tablets, or computers - blue light disrupts sleep", icon: X, color: "from-blue-400 to-indigo-400" },
  { id: "temp", title: "Cool the Room", description: "Set temperature to 65-68°F for optimal sleep", icon: Snowflake, color: "from-cyan-400 to-blue-400" },
  { id: "caffeine", title: "No Caffeine/Alcohol", description: "Both disrupt sleep architecture and worsen night sweats", icon: Coffee, color: "from-rose-400 to-pink-400" },
  { id: "shower", title: "Warm Shower/Bath", description: "The temperature drop afterward signals sleep time", icon: Droplets, color: "from-teal-400 to-emerald-400" },
  { id: "magnesium", title: "Take Magnesium (if prescribed)", description: "200-400mg magnesium glycinate can improve sleep quality", icon: Pill, color: "from-violet-400 to-purple-400" },
  { id: "journal", title: "Brain Dump Journal", description: "Write down tomorrow's worries to clear your mind", icon: BookOpen, color: "from-yellow-400 to-amber-400" },
  { id: "stretch", title: "Gentle Stretching", description: "Release physical tension from the day", icon: Dumbbell, color: "from-green-400 to-teal-400" },
  { id: "breathing", title: "5 Minutes Deep Breathing", description: "Calm your nervous system before bed", icon: Wind, color: "from-sky-400 to-cyan-400" },
  { id: "gratitude", title: "List 3 Gratitudes", description: "End the day with positive thoughts", icon: Heart, color: "from-pink-400 to-rose-400" },
];

export const SleepPrep = ({ onClose }: SleepPrepProps) => {
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [isComplete, setIsComplete] = useState(false);

  const handleToggle = (id: string) => {
    const newCompleted = new Set(completed);
    if (newCompleted.has(id)) {
      newCompleted.delete(id);
    } else {
      newCompleted.add(id);
    }
    setCompleted(newCompleted);
    
    if (newCompleted.size === checklist.length) {
      setIsComplete(true);
    }
  };

  const reset = () => {
    setCompleted(new Set());
    setIsComplete(false);
  };

  const progress = (completed.size / checklist.length) * 100;

  if (isComplete) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-12 text-center relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4"
          >
            <X className="h-5 w-5" />
          </Button>

          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-indigo-500 flex items-center justify-center shadow-lg animate-scale-in">
              <Moon className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4 text-foreground">Ready for Sleep!</h2>
            <p className="text-lg text-muted-foreground mb-2">
              You've completed all 10 steps of your wind-down routine.
            </p>
            <p className="text-muted-foreground">
              Your body and mind are prepared for restful sleep. Sweet dreams!
            </p>
          </div>

          <div className="flex gap-3 justify-center">
            <Button onClick={reset}>
              Start Again
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="max-w-2xl w-full py-8">
        <Button
          variant="ghost"
          onClick={onClose}
          className="mb-6 gap-2"
        >
          <X className="h-4 w-4" />
          Exit Routine
        </Button>

        <Card className="p-8 md:p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4 text-foreground">Sleep Preparation Routine</h2>
            <p className="text-muted-foreground mb-6">
              Complete these steps 1 hour before bed for optimal sleep quality
            </p>

            {/* Progress */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-muted-foreground mb-2">
                <span>{completed.size} of {checklist.length} completed</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 transition-all duration-500 shadow-sm"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Checklist */}
          <div className="space-y-4">
            {checklist.map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <div
                  key={item.id}
                  className={`flex items-start gap-4 p-5 rounded-xl border-2 transition-all duration-300 cursor-pointer group ${
                    completed.has(item.id)
                      ? "bg-primary-soft border-primary/50 shadow-sm"
                      : "bg-card border-border hover:border-primary/40 hover:shadow-md"
                  }`}
                  onClick={() => handleToggle(item.id)}
                >
                  {/* Icon with gradient background */}
                  <div className={`shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md transition-transform duration-300 ${completed.has(item.id) ? "scale-95 opacity-60" : "group-hover:scale-110"}`}>
                    <ItemIcon className="h-6 w-6 text-white" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        Step {index + 1}
                      </span>
                      {completed.has(item.id) && (
                        <Check className="h-4 w-4 text-primary animate-scale-in" />
                      )}
                    </div>
                    <h3 className={`font-bold text-lg mb-1 transition-all ${completed.has(item.id) ? "line-through text-muted-foreground" : "text-foreground"}`}>
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                  {/* Checkbox */}
                  <Checkbox
                    checked={completed.has(item.id)}
                    onCheckedChange={() => handleToggle(item.id)}
                    className="mt-2 shrink-0"
                  />
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <Button variant="outline" onClick={reset} size="sm">
              Reset Checklist
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
